// =============================================================================
// ESPOLIDAR WEB ADMIN - STATISTICS PAGE
// =============================================================================

import { useState } from 'react'
import { BarChart3, TrendingUp, Target, Calendar, Monitor } from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts'
import { useGlobalStats, useTimelineStats, useTopZones } from '../hooks/useStatistics'
import { useDevices } from '../hooks/useDevices'
import LoadingSpinner from '../components/LoadingSpinner'

// Colori per i grafici
const COLORS = {
  radar_enter: '#10B981',  // verde
  radar_exit: '#EF4444',   // rosso
  lidar_enter: '#3B82F6',  // blu
  lidar_exit: '#F59E0B',   // arancione
}

const PIE_COLORS = ['#10B981', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899']

const EVENT_LABELS: Record<string, string> = {
  radar_enter: 'Radar Entrata',
  radar_exit: 'Radar Uscita',
  lidar_enter: 'LIDAR Entrata',
  lidar_exit: 'LIDAR Uscita',
}

const PERIOD_OPTIONS = [
  { value: 7, label: 'Ultimi 7 giorni' },
  { value: 14, label: 'Ultimi 14 giorni' },
  { value: 30, label: 'Ultimi 30 giorni' },
  { value: 90, label: 'Ultimi 90 giorni' },
]

export default function StatisticsPage() {
  const [days, setDays] = useState(7)
  const [deviceFilter, setDeviceFilter] = useState<string>('')

  // Fetch data
  const { data: devices } = useDevices()
  const { data: globalStats, isLoading: loadingGlobal } = useGlobalStats(days)
  const { data: timeline, isLoading: loadingTimeline } = useTimelineStats({
    device_id: deviceFilter || undefined,
    days,
    bucket: 'day'
  })
  const { data: topZones, isLoading: loadingZones } = useTopZones({
    device_id: deviceFilter || undefined,
    days,
    limit: 10
  })

  // Prepara dati per grafici
  const eventsByType = globalStats?.by_type
    ? Object.entries(globalStats.by_type).map(([type, count]) => ({
        name: EVENT_LABELS[type] || type,
        value: count,
        type
      }))
    : []

  const timelineData = timeline?.data || []

  const topZonesData = topZones?.data || []

  // Calcola media giornaliera
  const avgPerDay = globalStats
    ? Math.round(globalStats.total_events / globalStats.period_days)
    : 0

  if (loadingGlobal) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Statistiche</h1>
        </div>

        {/* Filtri */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5 text-gray-400" />
            <select
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Tutti i dispositivi</option>
              {devices?.map((device) => (
                <option key={device.device_id} value={device.device_id}>
                  {device.name || device.device_id}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Totale Eventi</p>
              <p className="text-2xl font-bold text-gray-900">
                {globalStats?.total_events?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Media Giornaliera</p>
              <p className="text-2xl font-bold text-gray-900">
                {avgPerDay.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Monitor className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Dispositivi Attivi</p>
              <p className="text-2xl font-bold text-gray-900">
                {globalStats?.by_device?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary-600" />
          Trend Eventi
        </h2>
        {loadingTimeline ? (
          <div className="h-64 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : timelineData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value)
                  return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip
                labelFormatter={(value) => {
                  const date = new Date(value as string)
                  return date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
                }}
                formatter={(value, name) => [value ?? 0, EVENT_LABELS[name as string] || name]}
              />
              <Legend formatter={(value) => EVENT_LABELS[value] || value} />
              <Line type="monotone" dataKey="radar_enter" stroke={COLORS.radar_enter} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="radar_exit" stroke={COLORS.radar_exit} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="lidar_enter" stroke={COLORS.lidar_enter} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="lidar_exit" stroke={COLORS.lidar_exit} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Nessun dato disponibile per il periodo selezionato
          </div>
        )}
      </div>

      {/* Bottom Section: Events by Type + Top Zones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events by Type */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary-600" />
            Eventi per Tipo
          </h2>
          {eventsByType.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={eventsByType} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={120} />
                <Tooltip formatter={(value) => [Number(value ?? 0).toLocaleString(), 'Eventi']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {eventsByType.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[entry.type as keyof typeof COLORS] || PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Nessun dato disponibile
            </div>
          )}
        </div>

        {/* Top Zones */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary-600" />
            Top Zone Attivate
          </h2>
          {loadingZones ? (
            <div className="h-64 flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : topZonesData.length > 0 ? (
            <div className="space-y-3">
              {topZonesData.map((zone, index) => {
                const maxCount = topZonesData[0]?.count || 1
                const percentage = (zone.count / maxCount) * 100
                return (
                  <div key={`${zone.zone_id}-${zone.device_id}`} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-900">
                        {zone.zone_name}
                        <span className="text-gray-500 font-normal ml-2">
                          ({zone.device_name})
                        </span>
                      </span>
                      <span className="text-gray-600 font-semibold">{zone.count.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: PIE_COLORS[index % PIE_COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Nessun dato zone LIDAR disponibile
            </div>
          )}
        </div>
      </div>

      {/* Events by Device Table */}
      {globalStats?.by_device && globalStats.by_device.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Monitor className="h-5 w-5 text-primary-600" />
            Eventi per Dispositivo
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dispositivo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device ID
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eventi
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % Totale
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {globalStats.by_device.map((device) => (
                  <tr key={device.device_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {device.device_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {device.device_id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 font-semibold">
                      {device.count.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                      {globalStats.total_events > 0
                        ? ((device.count / globalStats.total_events) * 100).toFixed(1)
                        : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
